import { FC, useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import { IconXMark, Button, Box, Flex, Grid } from '@sequencehq/core-components'
import { InputSelectControlField } from 'Automations/nodeDefinitions/components'
import { useConditionEditing } from '../../hooks'
import { autoOpenConditionIdAtom } from '../../switch.atoms'

export const ConditionItem: FC<{
  nodeId: string
  conditionId: string
}> = ({ nodeId, conditionId }) => {
  const {
    condition,
    updateCondition,
    availableProperties,
    availableOperators,
    valueInputComponent: ValueInputComponent,
    propertyEditorComponent: PropertyEditorComponent,
    propertyEditorLayout,
    propertyOperators,
    requiresValue,
    gridStyle,
    siblingConditions,
    nodes,
    focusOperatorOnSelect,
    canRemove,
    onRemove,
    validationErrors
  } = useConditionEditing({
    nodeId,
    conditionId
  })

  const [autoOpenConditionId, setAutoOpenConditionId] = useAtom(
    autoOpenConditionIdAtom
  )
  const [initiallyOpen, setInitiallyOpen] = useState(
    conditionId === autoOpenConditionId
  )
  const [focusOnInitialLoad, setFocusOnInitialLoad] = useState(false)

  useEffect(() => {
    if (conditionId === autoOpenConditionId) {
      setAutoOpenConditionId(undefined)
    }
  }, [conditionId, autoOpenConditionId, setAutoOpenConditionId])

  useEffect(() => {
    if (!initiallyOpen) return
    setInitiallyOpen(false)
  }, [initiallyOpen])

  useEffect(() => {
    if (!focusOnInitialLoad) return
    setFocusOnInitialLoad(false)
  }, [focusOnInitialLoad])

  if (!condition) return null

  const propertyInput = (
    <InputSelectControlField
      data-testid={`automations.editor.configForm.switch.condition.${conditionId}.property`}
      placeholder="Select value..."
      options={availableProperties}
      value={condition.property}
      onChange={property => {
        updateCondition({ property })
        setFocusOnInitialLoad(true)
      }}
      validationErrors={validationErrors.property}
      initiallyOpen={initiallyOpen}
    />
  )

  const removeButton = canRemove ? (
    <Flex flexShrink={0} alignItems="center">
      <Button
        data-testid={`automations.editor.configForm.switch.condition.${conditionId}.removeCondition`}
        variant="secondary"
        icon={<IconXMark />}
        aria-label="Remove"
        onClick={onRemove}
      />
    </Flex>
  ) : null

  const propertyEditor = (() => {
    if (!condition.property) return null
    if (!PropertyEditorComponent || !propertyOperators) return null

    return (
      <PropertyEditorComponent
        data-testid={`automations.editor.configForm.switch.condition.${conditionId}`}
        propertyInput={
          propertyEditorLayout === 'inline' ? propertyInput : undefined
        }
        removeButton={
          propertyEditorLayout === 'inline' ? removeButton : undefined
        }
        nodes={nodes}
        operator={condition.operator}
        value={condition.value}
        onChange={({ operator, value }) =>
          updateCondition({
            ...(operator !== undefined ? { operator } : {}),
            ...(value !== undefined ? { value } : {})
          })
        }
        operators={propertyOperators}
        siblingConditions={siblingConditions}
        validationErrors={{
          operator: validationErrors.operator,
          value: validationErrors.value
        }}
        focusOnInitialLoad={focusOnInitialLoad}
      />
    )
  })()

  if (propertyEditorLayout === 'inline' && propertyEditor) {
    return <Box minWidth={0}>{propertyEditor}</Box>
  }

  return (
    <>
      <Flex gap="8px" alignItems="start">
        {propertyInput}
        {removeButton}
      </Flex>

      {condition.property &&
        (propertyEditor ? (
          propertyEditor
        ) : (
          <Grid
            gap="8px"
            gridTemplateColumns={
              // `fr` tracks have an auto minimum, so long labels can widen their columns.
              requiresValue ? 'minmax(0, 2fr) minmax(0, 3fr)' : 'minmax(0, 1fr)'
            }
            gridTemplateAreas='"operator"'
            {...gridStyle}
          >
            <Box gridArea="operator" minWidth={0}>
              <InputSelectControlField
                data-testid={`automations.editor.configForm.switch.condition.${conditionId}.operator`}
                placeholder="Select value..."
                options={availableOperators}
                value={condition.operator}
                disabled={!availableOperators.length}
                onChange={operator => updateCondition({ operator })}
                validationErrors={validationErrors.operator}
                initiallyOpen={
                  focusOnInitialLoad &&
                  focusOperatorOnSelect(condition.property)
                }
              />
            </Box>

            {requiresValue && ValueInputComponent && (
              <ValueInputComponent
                data-testid={`automations.editor.configForm.switch.condition.${conditionId}`}
                property={condition.property}
                value={condition.value}
                onChange={({ value }) => updateCondition({ value })}
                validationErrors={validationErrors.value}
                context={{
                  focusOnInitialLoad:
                    focusOnInitialLoad &&
                    !focusOperatorOnSelect(condition.property)
                }}
              />
            )}
          </Grid>
        ))}
    </>
  )
}
